/**
 * Mounts the floating browser preview over the chat column.
 *
 * Ported from t3code's ChatView integration: closing the preview panel on a
 * live browser floats it instead of dropping it, and the floater hides while
 * the same tab renders in the panel (`shouldRenderPreviewMiniPlayer`).
 * Adapted to forge: instead of ChatView calling the store inside its
 * close handler, the layer observes the right-panel surface transition, so
 * no route or ChatView wiring changes. The float triggers and their derived
 * state live in `useBrowserMiniPlayerTriggers.ts`.
 *
 * @module BrowserMiniPlayerLayer
 */
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import { useEffect } from "react";

import { selectBrowserThreadState, useBrowserStateStore } from "~/browserStateStore";
import { useRightPanelState } from "~/useRightPanel";

import { BrowserMiniPlayer } from "./BrowserMiniPlayer";
import {
  selectThreadBrowserMiniPlayer,
  shouldRenderBrowserMiniPlayer,
  useBrowserMiniPlayerStore,
} from "./browserMiniPlayerStore";
import {
  deriveBrowserMiniPlayerLayerState,
  useAutoShowBrowserMiniPlayer,
  useClearBrowserMiniPlayerOnSessionClose,
  useFloatBrowserOnPanelClose,
} from "./useBrowserMiniPlayerTriggers";

export interface BrowserMiniPlayerLayerProps {
  readonly threadRef: ScopedThreadRef;
}

export function BrowserMiniPlayerLayer({ threadRef }: BrowserMiniPlayerLayerProps) {
  const threadState = useBrowserStateStore((state) =>
    selectBrowserThreadState(state.browserStateByThreadKey, threadRef),
  );
  const threadKey = scopedThreadKey(threadRef);
  const { visibleSurface } = useRightPanelState(threadKey);
  const floating = useBrowserMiniPlayerStore((state) =>
    selectThreadBrowserMiniPlayer(state.byThreadKey, threadRef),
  );

  const { session, renderedPreviewSurfaceId, renderedPreviewTabId, activityTabId, latestActivity } =
    deriveBrowserMiniPlayerLayerState({ threadState, visibleSurface });

  useFloatBrowserOnPanelClose({
    threadRef,
    session,
    renderedSurfaceId: renderedPreviewSurfaceId,
    renderedTabId: renderedPreviewTabId,
  });

  // The thread route unmounted: the floater goes, but the user's dismissal
  // stays so a remount cannot resurrect the tab they closed.
  useEffect(
    () => () => {
      useBrowserMiniPlayerStore.getState().close(threadRef);
    },
    [threadRef],
  );

  // A closed session takes the floater and its suppression with it.
  useClearBrowserMiniPlayerOnSessionClose({ threadRef, session });

  useAutoShowBrowserMiniPlayer({
    threadRef,
    session,
    activityTabId,
    latestActivity,
    renderedInPanel: renderedPreviewSurfaceId !== null,
  });

  return floating !== null && shouldRenderBrowserMiniPlayer(floating.source, visibleSurface) ? (
    <BrowserMiniPlayer threadRef={threadRef} miniPlayer={floating} />
  ) : null;
}
